import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';

const VendorContext = createContext();

export const useVendor = () => {
  const context = useContext(VendorContext);
  if (!context) {
    throw new Error('useVendor must be used within VendorProvider');
  }
  return context;
};

export const VendorProvider = ({ children }) => {
  const [vendor, setVendor] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  const API_URL = process.env.REACT_APP_API_URL || 'https://api.paceestate.com';

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('vendor_token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/vendor/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVendor(response.data);
      setIsAuthenticated(true);
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('vendor_token');
      setIsAuthenticated(false);
      setVendor(null);
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/api/vendor/login`, {
        email,
        password
      });

      const { access_token, user } = response.data;
      localStorage.setItem('vendor_token', access_token);
      setVendor(user);
      setIsAuthenticated(true);
      return { success: true };
    } catch (error) {
      console.error('Login failed:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Login failed'
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('vendor_token');
    setVendor(null);
    setIsAuthenticated(false);
  };

  const updateProfile = async (data) => {
    const token = localStorage.getItem('vendor_token');
    try {
      const response = await axios.put(`${API_URL}/api/vendor/profile`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVendor(response.data);
      return { success: true };
    } catch (error) {
      console.error('Profile update failed:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Update failed'
      };
    }
  };

  const value = {
    vendor,
    isAuthenticated,
    loading,
    login,
    logout,
    updateProfile,
    checkAuth
  };

  return (
    <VendorContext.Provider value={value}>
      {children}
    </VendorContext.Provider>
  );
};
